import useNonInitialEffect from '@versiondos/hooks';
import { status as AppointmentStatus } from '@spike/appointment-model';
import { Period, PageResult } from '@spike/model';
import { NewBookingsStatus } from '@spike/new-bookings-action';
import { Room } from '@spike/room-model';
import { Task, TaskPagination, TasksPendingCount } from '@spike/task-model';
import { bulkCompleteTaskThunk, fetchTasksPendingCountThunk, searchTasksThunk, TasksStatus } from '@spike/tasks-action';
import { useApiClientWrapper } from 'hooks';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import styles from './Dashboard.module.scss';
import DashboardHeader, { DatePeriod } from './DashboardHeader';
import TasksTable, { TasksSortField } from './TasksTable';
import { CARE_TASK_TYPES, CARE_TYPE_OPTIONS } from '../careTaskTypes';
import { TasksDatePeriod } from '../TasksLayout';
import { nextColumnSort, TableColumnSort } from 'utils/tableSort';

export type TaskCategory = 'feeding' | 'medication' | 'care';

const CATEGORY_TASK_TYPE: Record<TaskCategory, number | undefined> = {
    feeding: 1,
    medication: 2,
    care: undefined
};

const COMPLETE_ALL_TABS = ['upcoming', 'past_due'];

const tabToStatus = (tabId: string): string | undefined => (tabId === 'all' ? undefined : tabId);

const emptyVariant = (tabId: string): 'upcoming' | 'completed' | 'all' =>
    tabId === 'completed' ? 'completed' : tabId === 'all' ? 'all' : 'upcoming';

interface TaskCategoryDashboardProps {
    category: TaskCategory;
    datePeriod: TasksDatePeriod;
    onChangeDate: (period: TasksDatePeriod) => void;
}

export const TaskCategoryDashboard: FunctionComponent<TaskCategoryDashboardProps> = ({
    category,
    datePeriod,
    onChangeDate
}) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const { organizationMarketplaces } = useOrganizationMarketplaces();

    const isCare = category === 'care';

    const tasks = useSelector<RootState, Array<Task>>(state => state.tasks.tasks);
    const tasksStatus = useSelector<RootState, TasksStatus>(state => state.tasks.status);
    const pagination = useSelector<RootState, TaskPagination | undefined>(state => state.tasks.pagination);
    const pendingCount = useSelector<RootState, TasksPendingCount | undefined>(state => state.tasks.tasksPendingCount);
    const newBookingsStatus = useSelector<RootState, NewBookingsStatus>(state => state.newBookings.status);
    const rooms = useSelector<RootState, PageResult<Room>>(state => state.rooms.rooms);

    const [searching, setSearching] = useState(true);
    const searchSeq = useRef(0);

    const [search, setSearch] = useState('');
    const [selectedTab, setSelectedTab] = useState('upcoming');
    const [selectedRoomIds, setSelectedRoomIds] = useState<Array<number>>([]);
    const [selectedTaskTypeIds, setSelectedTaskTypeIds] = useState<Array<number>>([]);
    const [selectedTaskRange, setSelectedTaskRange] = useState<string | undefined>(undefined);
    const [selectedMarketplaceIds, setSelectedMarketplaceIds] = useState<Array<number>>([]);
    const [sort, setSort] = useState<TableColumnSort<TasksSortField> | undefined>(undefined);

    const showLocationColumn = organizationMarketplaces.length >= 2;

    const marketplaceIdsKey = organizationMarketplaces.map(marketplace => marketplace.id).join(',');
    const marketplaceIds = useMemo(
        () => organizationMarketplaces.map(marketplace => marketplace.id).filter((id): id is number => id != null),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [marketplaceIdsKey]
    );

    // Empty selection means "all locations"; the filter always sends concrete ids once touched.
    const resolveMarketplaceIds = (override?: Array<number>): Array<number> => {
        const selected = override ?? selectedMarketplaceIds;
        return selected.length > 0 ? selected : marketplaceIds;
    };

    const status = tabToStatus(selectedTab);

    // Feeding/meds are a single fixed type. Care lists every non-feeding/meds type
    // (hydration, relief, add-on); the type chips narrow it to a subset.
    const resolveTaskType = (typeIdsOverride?: Array<number>): number | Array<number> | undefined => {
        if (!isCare) {
            return CATEGORY_TASK_TYPE[category];
        }
        const ids = typeIdsOverride ?? selectedTaskTypeIds;
        return ids.length > 0 ? ids : CARE_TASK_TYPES;
    };

    const search_ = (
        page: number,
        size: number | undefined,
        overrides?: {
            query?: string;
            status?: string;
            period?: Period;
            roomIds?: Array<number>;
            taskRange?: string;
            taskTypeIds?: Array<number>;
            marketplaceIds?: Array<number>;
            sortBy?: string;
            sortOrder?: string;
        }
    ) => {
        const effectiveStatus = overrides && 'status' in overrides ? overrides.status : status;
        const effectiveTaskRange = overrides && 'taskRange' in overrides ? overrides.taskRange : selectedTaskRange;
        const effectiveSortBy = overrides && 'sortBy' in overrides ? overrides.sortBy : sort?.field;
        const effectiveSortOrder = overrides && 'sortOrder' in overrides ? overrides.sortOrder : sort?.direction;
        // Track loading locally; the counts fetch clobbers the shared redux status mid-search.
        const seq = ++searchSeq.current;
        setSearching(true);
        Promise.resolve(
            dispatch(
                searchTasksThunk(
                    apiClientWrapper,
                    page,
                    size,
                    overrides?.query ?? search,
                    overrides?.period ?? datePeriod,
                    effectiveStatus,
                    resolveTaskType(overrides?.taskTypeIds),
                    resolveMarketplaceIds(overrides?.marketplaceIds),
                    overrides?.roomIds ?? selectedRoomIds,
                    effectiveTaskRange,
                    effectiveSortBy,
                    effectiveSortOrder
                )
            )
        ).finally(() => {
            if (searchSeq.current === seq) {
                setSearching(false);
            }
        });
    };

    const fetchCounts = (overrides?: {
        period?: Period;
        roomIds?: Array<number>;
        query?: string;
        taskRange?: string;
        taskTypeIds?: Array<number>;
        marketplaceIds?: Array<number>;
    }) => {
        const effectiveTaskRange = overrides && 'taskRange' in overrides ? overrides.taskRange : selectedTaskRange;
        dispatch(
            fetchTasksPendingCountThunk(
                apiClientWrapper,
                overrides?.period ?? datePeriod,
                resolveMarketplaceIds(overrides?.marketplaceIds),
                overrides?.roomIds ?? selectedRoomIds,
                resolveTaskType(overrides?.taskTypeIds),
                overrides?.query ?? search,
                effectiveTaskRange
            )
        );
    };

    const initialized = useRef(false);
    useEffect(() => {
        if (initialized.current) {
            return;
        }
        initialized.current = true;
        search_(1, undefined);
        fetchCounts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearch = (value: string) => {
        setSearch(value);
        search_(1, pagination?.size, { query: value });
        fetchCounts({ query: value });
    };

    const handleChangeTab = (tabId: string) => {
        setSelectedTab(tabId);
        search_(1, pagination?.size, { status: tabToStatus(tabId) });
        fetchCounts();
    };

    const handleChangeDate = (period: DatePeriod) => {
        onChangeDate(period);
        search_(1, pagination?.size, { period });
        fetchCounts({ period });
    };

    const handleChangeRooms = (roomIds: Array<number>) => {
        setSelectedRoomIds(roomIds);
        search_(1, pagination?.size, { roomIds });
        fetchCounts({ roomIds });
    };

    const handleChangeTaskTypes = (taskTypeIds: Array<number>) => {
        setSelectedTaskTypeIds(taskTypeIds);
        search_(1, pagination?.size, { taskTypeIds });
        fetchCounts({ taskTypeIds });
    };

    const handleChangeTaskRange = (range: string | undefined) => {
        setSelectedTaskRange(range);
        search_(1, pagination?.size, { taskRange: range });
        fetchCounts({ taskRange: range });
    };

    const handleChangeMarketplaces = (ids: Array<number>) => {
        setSelectedMarketplaceIds(ids);
        search_(1, pagination?.size, { marketplaceIds: ids });
        fetchCounts({ marketplaceIds: ids });
    };

    const handlePageChange = (page: number) => search_(page, pagination?.size);

    const handleSizeChange = (size: number) => search_(1, size);

    const handleSort = (field: TasksSortField) => {
        const nextSort = nextColumnSort(field, sort);
        setSort(nextSort);
        search_(1, pagination?.size, { sortBy: nextSort.field, sortOrder: nextSort.direction });
    };

    const completeAll = (taskIds: Array<number>) => {
        if (taskIds.length > 0) {
            dispatch(bulkCompleteTaskThunk(apiClientWrapper, taskIds));
        }
    };

    // Reload list AND counts together so the tab badges never drift from what's shown
    // (e.g. after completing the last upcoming task).
    const reload = () => {
        search_(pagination?.page ?? 1, pagination?.size);
        fetchCounts();
    };

    useNonInitialEffect(() => {
        switch (tasksStatus) {
            case TasksStatus.CompleteSuccess:
            case TasksStatus.UncompleteSuccess:
            case TasksStatus.BulkCompleteSuccess: {
                const timer = setTimeout(reload, 600);
                return () => clearTimeout(timer);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tasksStatus]);

    useNonInitialEffect(() => {
        if (newBookingsStatus === NewBookingsStatus.SingleRescheduleSuccess) {
            const timer = setTimeout(reload, 400);
            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newBookingsStatus]);

    const visibleTasks = useMemo(
        () =>
            tasks.filter(
                task =>
                    !(
                        task.appointmentStatus === AppointmentStatus.CANCELLED ||
                        task.appointmentStatus === AppointmentStatus.NO_SHOW
                    )
            ),
        [tasks]
    );

    const isLoading = searching;

    return (
        <div className={styles.container}>
            <DashboardHeader
                search={search}
                category={category}
                selectedTabId={selectedTab}
                datePeriod={datePeriod}
                selectedRoomIds={selectedRoomIds}
                pendingCount={pendingCount}
                taskTypeOptions={isCare ? CARE_TYPE_OPTIONS : undefined}
                selectedTaskTypeIds={selectedTaskTypeIds}
                selectedTaskRange={selectedTaskRange}
                selectedMarketplaceIds={resolveMarketplaceIds()}
                onSearch={handleSearch}
                onChangeTab={handleChangeTab}
                onChangeDate={handleChangeDate}
                onChangeRooms={handleChangeRooms}
                onChangeTaskTypes={handleChangeTaskTypes}
                onChangeTaskRange={handleChangeTaskRange}
                onChangeMarketplaces={handleChangeMarketplaces}
            />
            <div className={styles.body}>
                <TasksTable
                    tasks={visibleTasks}
                    rooms={rooms.page}
                    isLoading={isLoading}
                    showLocationColumn={showLocationColumn}
                    showCompleteAll={COMPLETE_ALL_TABS.includes(selectedTab)}
                    variant={status}
                    emptyVariant={emptyVariant(selectedTab)}
                    sort={sort}
                    onSort={handleSort}
                    pagination={
                        pagination
                            ? { page: pagination.page, size: pagination.size, totalPages: pagination.totalPages }
                            : undefined
                    }
                    onPageChange={handlePageChange}
                    onSizeChange={handleSizeChange}
                    onCompleteAll={completeAll}
                />
            </div>
        </div>
    );
};

export default TaskCategoryDashboard;
